import { onDocumentLoaded, prefersReducedMotion } from '@theme/utilities';

/**
 * Email Signup Block Component
 *
 * Handles dynamic padding adjustment for email signup input fields
 * when integrated buttons have variable widths.
 */
class EmailSignupBlock extends HTMLElement {
  /** @type {boolean} */
  static #scrollHandled = false;

  constructor() {
    super();
    /** @type {number} */
    this.lastButtonWidth = 0;
    /** @type {ResizeObserver | undefined} */
    this.observer = undefined;

    this.adjustInputPadding = this.adjustInputPadding.bind(this);
  }

  connectedCallback() {
    this.button = this.querySelector('.email-signup__button--integrated');
    this.input = this.querySelector('.email-signup__input');
    this.inputGroup = this.querySelector('.email-signup__input-group');

    const isTextButton = this.button && this.button.classList.contains('email-signup__button--text');

    if (this.button && this.input && this.inputGroup && isTextButton) {
      this.adjustInputPadding();

      this.observer = new ResizeObserver(() => {
        this.adjustInputPadding();
      });
      this.observer.observe(this.button);

      if ('fonts' in document) {
        document.fonts.ready.then(() => {
          this.adjustInputPadding();
        });
      }
    }

    this.scrollToBlock();
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  adjustInputPadding() {
    if (!this.button || !this.inputGroup) return;

    const buttonWidth = /** @type {HTMLElement} */ (this.button).offsetWidth;

    if (buttonWidth !== this.lastButtonWidth) {
      this.lastButtonWidth = buttonWidth;
      /** @type {HTMLElement} */ (this.inputGroup).style.setProperty('--button-actual-width', `${buttonWidth}px`);
    }
  }

  scrollToBlock() {
    if (EmailSignupBlock.#scrollHandled) return;

    const hasContactHash = window.location.hash === '#contact_form';
    const customerPosted = new URLSearchParams(window.location.search).has('customer_posted');

    if (!hasContactHash && !customerPosted) return;

    const form = this.querySelector('#contact_form, .email-signup__form');
    if (!form) return;

    if (hasContactHash && !this.querySelector('#contact_form')) return;

    const feedback = this.querySelector('.email-signup__message--success, .email-signup__message--error');
    if (customerPosted && !feedback) return;

    EmailSignupBlock.#scrollHandled = true;

    const scrollToTarget = () => {
      const headerHeight =
        parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height')) || 0;
      const offset = headerHeight + 16;
      const top = this.getBoundingClientRect().top + window.scrollY - offset;

      window.scrollTo({
        top: Math.max(0, top),
        behavior: prefersReducedMotion() ? 'instant' : 'smooth',
      });

      if (feedback instanceof HTMLElement) {
        feedback.focus({ preventScroll: true });
      }
    };

    onDocumentLoaded(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(scrollToTarget);
      });
    });
  }
}

if (!customElements.get('email-signup-block')) {
  customElements.define('email-signup-block', EmailSignupBlock);
}
